// MUI
import { Box } from "@mui/material";
import { isNumber } from "@mui/x-data-grid-pro/internals";
// Third Party
import _ from "lodash";

// Components
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";

/********************************************* HELPERS ************************************************/
/**
 * Compares two values based on the operator
 * @param { any } value1
 * @param { any } value2
 * @param { string } operator
 * @returns {boolean}
 */
function compareValues(value1, value2, operator) {
	switch (operator) {
		case "eq":
			return _.isEqual(value1, value2);
		case "gt":
			return _.gt(value1, value2);
		case "lt":
			return _.lt(value1, value2);
		case "gte":
			return _.gte(value1, value2);
		case "lte":
			return _.lte(value1, value2);
		case "ne":
			return !_.isEqual(value1, value2);
		default:
			return false;
	}
}

/**
 *
 * @param values - array of all table rows
 * @param fieldsToFilter - array of objects with the fields and their values to exclude
 * @param {string} exclusionValue - value to exclude
 * @returns {Array} - array of rows that should be included
 */
const getExcludedRows = (values, fieldsToFilter, exclusionValue = "") => {
	if (!values.length) {
		return [];
	}

	return values.filter((value) => {
		return fieldsToFilter.every(({ excludeField, comparisonOperator, excludeValue }) =>
			compareValues(value[excludeField], exclusionValue || excludeValue, comparisonOperator)
		);
	});
};

const calculateExclusiveSummation = ({ values, field, fieldsToFilter, exclusionValue = "" }) => {
	if (!values.length) {
		return 0;
	}

	// Case 1: fieldsToFilter is an array of conditions
	if (Array.isArray(fieldsToFilter)) {
		const rowsToCalculate = getExcludedRows(values, fieldsToFilter, exclusionValue);

		return rowsToCalculate.reduce((acc, row) => {
			if (!row) {
				return acc;
			}
			// Prefer the flattened cell value if it exists
			if (row[field] != null && !Number.isNaN(+row[field])) {
				return acc + +row[field];
			}

			// Fallback: old-style nested products access
			if (field.includes("_")) {
				const [productType, ...rest] = field.split("_");
				const columnType = rest.join("_");
				const prod = row[productType]?.find((p) => p?.product === columnType);
				return acc + (prod?.value ? +prod.value : 0);
			}

			return acc;
		}, 0);
	}

	// Case 2: fieldsToFilter is a single field name
	return values.reduce((total, row) => {
		if (!row) {
			return total;
		}

		// Respect exclusion filter for ALL fields (including front/back products)
		if (fieldsToFilter && row[fieldsToFilter] === exclusionValue) {
			return total;
		}

		// 1) Prefer using the column's actual value (what valueGetter produced)
		if (row[field] != null && !Number.isNaN(+row[field])) {
			return total + +row[field];
		}

		// 2) Fallback: reconstruct from nested products using the prefix
		if (field.includes("_")) {
			const [productType, ...rest] = field.split("_");
			const columnType = rest.join("_");
			const prod = row[productType]?.find((p) => p?.product === columnType);
			return total + (prod?.value ? +prod.value : 0);
		}

		return total;
	}, 0);
};

export default function useAggregationExtended(props) {
	const conditionalSum = {
		apply: ({ values }) => {
			if (!values.length) {
				return "";
			}
			let total = 0;
			for (const value of values) {
				if (
					(props.conditionalSum?.operation === "NOT EQUAL" && value !== props.conditionalSum?.condition) ||
					value === props.conditionalSum?.condition
				) {
					total += 1;
				}
			}
			return `${props.conditionalSum?.title || ""} ${total}`;
		},
		valueFormatter: (value) => {
			if (value == null || !isNumber(value)) {
				return value;
			}

			return value.toLocaleString();
		},
		hasCellUnit: false,
	};

	const uniqueCount = {
		apply: ({ values }) => {
			if (!values.length) {
				return "";
			}
			return [...new Set(values)].length.toString();
		},
		valueFormatter: (value) => {
			if (value == null || !isNumber(value)) {
				return value;
			}
			return value.toLocaleString();
		},
		hasCellUnit: false,
	};

	const technicianUniqueCount = {
		apply: ({ values }) => {
			if (!values.length) {
				return "";
			}
			return [...new Set(values)].length.toString();
		},
		renderCell: (params) => {
			if (params?.id?.includes?.("auto-generated-group-footer-root")) {
				return "";
			}
			const value = +(params?.value || 0);
			return `(${value})`;
		},
		hasCellUnit: false,
	};

	const selectedSum = {
		getCellValue: ({ row }) => row,
		apply: ({ values, field }) => {
			if (!values.length) {
				return 0;
			}
			let total = 0;
			values.forEach((value) => {
				if (!props.selectedSum?.onlySelected || props.selectedSum?.checkConditions?.(value)) {
					const rowValue = +value[field] || +props.selectedSum?.getCustomValue?.(value, field) || 0;
					total += rowValue;
				}
			});
			return total;
		},
		valueFormatter: (value) => {
			if (value == null || !isNumber(value)) {
				return value;
			}

			return value.toLocaleString();
		},
		hasCellUnit: false,
	};

	const selectedAvg = {
		apply: ({ values }) => {
			if (!values.length) {
				return 0;
			}
			let total = 0;
			let count = 0;
			values.forEach((value) => {
				if (!props.selectedSum?.onlySelected || value.condition) {
					total += value.cellValue;
					count++;
				}
			});
			return count === 0 ? 0 : total / count;
		},
		valueFormatter: (value) => {
			if (value == null || !isNumber(value)) {
				return value;
			}

			return value.toLocaleString();
		},
		hasCellUnit: false,
	};

	const selectedMedian = {
		getCellValue: ({ row }) => row,
		apply: ({ values, field }) => {
			if (!values.length) {
				return 0;
			}
			const valuesToConsider = values?.map?.((value) => +value?.[field]).sort();
			let result = valuesToConsider[Math.floor(valuesToConsider.length / 2)];
			if (valuesToConsider?.length % 2 === 0) {
				result =
					(valuesToConsider[Math.floor(valuesToConsider.length / 2)] +
						valuesToConsider[Math.floor(valuesToConsider.length / 2) - 1]) /
					2;
			}
			return result;
		},
		valueFormatter: (value) => {
			if (value == null || !isNumber(value)) {
				return value;
			}

			return value.toLocaleString();
		},
		hasCellUnit: false,
	};

	const accumulatePositiveTrades = (trades = []) => trades.reduce((acc, cur) => acc + cur.units, 0);

	const conditionalCount = {
		getCellValue: ({ row }) => row,
		apply: ({ values: tableRows }) => {
			const condition = props.conditionalCount?.condition;

			const fieldsToFilter = props.conditionalCount?.fieldsToFilter || [];

			if (fieldsToFilter.length) {
				const excludedRows = tableRows?.filter((row) => {
					return fieldsToFilter.every(({ excludeField, comparisonOperator, excludeValue }) => {
						return compareValues(row[excludeField], excludeValue, comparisonOperator);
					});
				});

				return accumulatePositiveTrades(excludedRows);
			}

			const includedRows = tableRows?.filter((value) =>
				compareValues(value.tradeACV, condition, props.conditionalCount?.operation?.toLowerCase())
			);

			return accumulatePositiveTrades(includedRows);
		},
		hasCellUnit: false,
	};

	const exclusiveSummation = {
		getCellValue: ({ row }) => row,
		apply: ({ values, field }) => {
			const { fieldsToFilter, exclusionValue } = props?.exclusiveSummation ?? {};

			return calculateExclusiveSummation({
				values,
				field,
				fieldsToFilter,
				exclusionValue,
			});
		},
	};

	const technicianCustomRate = {
		getCellValue: ({ row }) => row,
		apply: (params) => {
			const { values, groupId } = params;
			if (!values.length) {
				return 0;
			}
			const uniqueCountMap = new Map();
			let extended = 0;
			let bookedTime = 0;
			values
				.filter?.((item) => !["N"].includes(item?.payrollStatus))
				?.forEach?.((value) => {
					extended += +(value?.["extended"] || 0);
					bookedTime += +(value?.["bookedTime"] || 0);
					if (value?.["rate"]) {
						const rate = value?.["rate"];
						if (uniqueCountMap.has(rate)) {
							uniqueCountMap.set(rate, uniqueCountMap.get(rate) + 1);
						} else {
							uniqueCountMap.set(rate, 1);
						}
					}
				});
			if (String(groupId).startsWith("auto-") || !groupId?.length) {
				return extended / (bookedTime || 1);
			}
			return uniqueCountMap.size;
		},
		valueFormatter: (value) => {
			if (value == null || !isNumber(value)) {
				return value;
			}

			return +Number(value).toFixed(2);
		},
		hasCellUnit: false,
	};

	const technicianCustomSum = {
		getCellValue: ({ row }) => row,
		apply: ({ values, field }) => {
			if (!values.length) {
				return 0;
			}
			let total = 0;
			values
				.filter?.((item) => !["N"].includes(item?.payrollStatus))
				?.forEach?.((value) => {
					if (
						!props.selectedSum?.onlySelected ||
						(props.selectedSum?.checkConditions && props.selectedSum?.checkConditions(value))
					) {
						const rowValue =
							+value[field] || +(props.selectedSum?.getCustomValue && props.selectedSum?.getCustomValue(value, field)) || 0;
						total += rowValue;
					}
				});
			return total;
		},
		valueFormatter: (value) => {
			if (value == null || !isNumber(value)) {
				return value;
			}

			return value.toLocaleString();
		},
		hasCellUnit: false,
	};

	const departmentalTableSum = {
		getCellValue: ({ row }) => row,
		apply: ({ values, field }) => {
			if (!values.length) {
				return 0;
			}
			let fieldToUse = field;
			let total = 0;
			let valuesToUse = [];
			if (field === "contracts") {
				fieldToUse = "units";
				valuesToUse = values?.filter?.((item) => item?.isFooter);
				if (!valuesToUse?.length) {
					valuesToUse = values.filter?.((item) => item?.subFooter || item?.footer);
				}
			} else {
				valuesToUse = values.filter?.((item) => !item?.subFooter && !item?.isFooter && !item?.footer);
			}
			valuesToUse?.forEach?.((value) => {
				const rowValue = +value?.[fieldToUse] || 0;
				total += rowValue;
			});
			return total;
		},
		valueFormatter: (value) => {
			if (value == null || !isNumber(value)) {
				return value;
			}

			return value.toLocaleString();
		},
		hasCellUnit: false,
	};
	const departmentalAccountPvr = {
		getCellValue: ({ row }) => row,
		apply: (params) => {
			const { values } = params;
			if (!values.length) {
				return 0;
			}
			let units = 0;
			let grossProfit = 0;
			const regularRows = values.filter?.((item) => !item?.subFooter && !item?.isFooter && !item?.footer);
			regularRows?.forEach?.((value) => {
				units += +(value?.units ?? 0) + +(value?.retailUnits ?? 0) + +(value?.leaseUnits ?? 0);
				grossProfit += +(value?.grossProfit ?? 0) + +(value?.retailGrossProfit ?? 0) + +(value?.leaseGrossProfit ?? 0);
			});
			return +Number(grossProfit / (units || 1)).toFixed(2);
		},
		valueFormatter: (value) => {
			if (value == null || !isNumber(value)) {
				return value;
			}
			const isNegative = value < 0;
			return isNegative ? `(${Math.abs(value).toFixed(2)})` : value.toFixed(2);
		},
		hasCellUnit: false,
	};

	const departmentalAccountPvrWithContracts = {
		getCellValue: ({ row }) => row,
		apply: (params) => {
			const { values } = params;
			if (!values.length) {
				return 0;
			}
			let units = 0;
			let grossProfit = 0;
			const regularRows = values.filter?.((item) => !item?.subFooter && !item?.isFooter && !item?.footer);
			const subFooterRows = values.filter?.((item) => item?.subFooter);
			regularRows?.forEach?.((value) => {
				grossProfit += +(value?.grossProfit ?? 0) + +(value?.retailGrossProfit ?? 0) + +(value?.leaseGrossProfit ?? 0);
			});
			subFooterRows?.forEach?.((value) => {
				units += +(value?.units ?? 0) + +(value?.retailUnits ?? 0) + +(value?.leaseUnits ?? 0);
			});
			return +Number(grossProfit / (units || 1)).toFixed(2);
		},
		valueFormatter: (value) => {
			if (value == null || !isNumber(value)) {
				return value;
			}
			return +Number(value).toFixed(2);
		},
		hasCellUnit: false,
	};

	const avgNumberBetweenDates = {
		apply: ({ values }) => {
			if (!values.length) {
				return "";
			}
			const saleDateArr = props.avgNumberBetweenDates.data;
			if (values.length === saleDateArr.length) {
				const { sum, total } = values.reduce(
					(acc, item, index) => {
						if (item !== null && saleDateArr[index] !== null) {
							const valueSaleDate = new Date(item);
							const valueDate = new Date(saleDateArr[index]);
							if (valueDate.getUTCFullYear() === valueSaleDate.getUTCFullYear()) {
								return { sum: acc.sum + (valueDate.getUTCMonth() - valueSaleDate.getUTCMonth()), total: acc.total + 1 };
							}
						}
						return acc;
					},
					{ sum: 0, total: 0 }
				);

				return Math.round(sum / total).toString();
			} else {
				return "";
			}
		},
		valueFormatter: (value) => {
			if (value == null || !isNumber(value)) {
				return value;
			}
			return value.toLocaleString();
		},
		hasCellUnit: false,
	};

	const exclusiveAvg = {
		getCellValue: ({ row }) => row,
		apply: ({ values, field }) => {
			const total = calculateExclusiveSummation({
				values,
				field,
				fieldsToFilter: props?.exclusiveAvg?.fieldsToFilter,
			});

			let count;
			if (Array.isArray(props?.exclusiveAvg?.fieldsToFilter)) {
				const excludedRows = getExcludedRows(values, props?.exclusiveAvg?.fieldsToFilter);
				count = excludedRows.length;
			} else {
				count = values?.filter(
					(value) => value?.[props?.exclusiveAvg?.fieldsToFilter] !== props?.exclusiveAvg?.excludeValue
				).length;
			}

			return count === 0 ? 0 : total / count;
		},
	};

	// Returns the repair order count footer in the following format:
	// 	totalRepairOrders (repairOrderCountSum) where:
	// 		repairOrderCountSum - sum of roCount values
	// 		totalRepairOrders - total number of roCount rows that equal 0 + repairOrderCountSum
	const repairOrderCountAggregation = {
		apply: ({ values }) => {
			if (!values.length) {
				return { totalRepairOrders: 0, repairOrderCountSum: 0 };
			}
			let total = 0;
			let totalZeroRows = 0;
			for (const value of values) {
				if (value) {
					total += value;
				} else {
					totalZeroRows++;
				}
			}
			return { totalRepairOrders: totalZeroRows + total, repairOrderCountSum: total };
		},
		valueFormatter: (value) => {
			if (value == null || typeof value !== "object" || !("totalRepairOrders" in value) || !("repairOrderCountSum" in value)) {
				return value;
			}

			const { totalRepairOrders, repairOrderCountSum } = value;
			return `${totalRepairOrders.toLocaleString()} (${repairOrderCountSum.toLocaleString()})`;
		},
		hasCellUnit: false,
	};

	const repairOrderFinalizedOrdersAggregation = {
		getCellValue: ({ row }) => row,
		apply: ({ values }) => {
			if (!values.length) {
				return { finalizedOrders: 0, totalOrders: 0 };
			}
			const totalOrders = values.length;
			const finalizedOrders = values.filter((row) => row?.repairOrder?.repairOrderRecord?.rogen?.repairOrderStatus === "F");
			const finalizedOrdersAmount = finalizedOrders.length;
			return { finalizedOrders: finalizedOrdersAmount, totalOrders };
		},
		valueFormatter: (value) => {
			if (value == null || typeof value !== "object") {
				return value;
			}

			const { finalizedOrders, totalOrders } = value;
			const text = (
				<Box component={"span"} className={"text-white !font-montserrat"}>
					{`${totalOrders} (${finalizedOrders})`}
				</Box>
			);
			return (
				<TypographyCell
					isFooter
					tooltipProps={{ title: "Total (Finalized)" }}
					forceTooltip
					marginTop={-0.5}
					className={"!font-montserrat"}
				>
					{text}
				</TypographyCell>
			);
		},
		hasCellUnit: false,
	};

	return {
		avgNumberBetweenDates,
		conditionalCount,
		conditionalSum,
		departmentalAccountPvr,
		departmentalAccountPvrWithContracts,
		departmentalTableSum,
		exclusiveAvg,
		exclusiveSummation,
		repairOrderCountAggregation,
		repairOrderFinalizedOrdersAggregation,
		selectedAvg,
		selectedMedian,
		selectedSum,
		technicianCustomRate,
		technicianCustomSum,
		technicianUniqueCount,
		uniqueCount,
	};
}
